import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Edit, Trash2, Plus, Search, FileCheck, CheckCircle2, AlertCircle, Eye, Home, ChevronRight, Users } from 'lucide-react';
import { Cliente, fetchClientes } from '../../../services/clienteService';
import AddClienteModal from '../../../components/Clients/AddClienteModal';
import EditClienteModal from '../../../components/Clients/EditClienteModal';
import DeleteClienteModal from '../../../components/Clients/DeleteClienteModal';
import ModalVerDeclaracionJurada from '../../../components/Clients/ModalVerDeclaracionJurada';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const ClientsPage: React.FC = () => {
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [clienteToEdit, setClienteToEdit] = useState<Cliente | null>(null);
    const [clienteToDelete, setClienteToDelete] = useState<Cliente | null>(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [declaracionModalOpen, setDeclaracionModalOpen] = useState(false);
    const [clienteDeclaracion, setClienteDeclaracion] = useState<{
        nombre?: string;
        email?: string;
        telefono?: string | null;
        ci_nit?: string | null;
        tipo_cliente?: string;
        fecha_declaracion_jurada?: string | null;
        foto_declaracion_jurada_url?: string | null;
    } | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('cliente', userLevelId);

    const loadClientes = useCallback(async () => {
        if (!perm?.can_read) {
            setClientes([]);
            return;
        }
        try {
            const data = await fetchClientes();
            const sortedData = data.sort((a, b) => a.id - b.id);
            setClientes(sortedData);
        } catch (err) {
            console.error('Error fetching clientes:', err);
            setClientes([]);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadClientes();
    }, [loadClientes]);

    const openEditModal = (cliente: Cliente) => {
        setClienteToEdit(cliente);
        setIsEditOpen(true);
    };

    const handleUpdateCliente = (updated: Cliente) => {
        setClientes(prev => {
            const updatedList = prev.map(c => (c.id === updated.id ? updated : c));
            return updatedList.sort((a, b) => a.id - b.id);
        });
    };

    const handleAddCliente = (nuevoCliente: Cliente) => {
        setClientes(prev => {
            const updated = [...prev, nuevoCliente];
            return updated.sort((a, b) => a.id - b.id);
        });
    };

    const openDeleteModal = (cliente: Cliente) => {
        setClienteToDelete(cliente);
        setIsDeleteOpen(true);
    };

    const handleClienteDeleted = (deletedId: number) => {
        setClientes(prev => {
            const filteredList = prev.filter(c => c.id !== deletedId);
            return filteredList.sort((a, b) => a.id - b.id);
        });
    };

    const handleVerDeclaracion = (cliente: Cliente) => {
        const nombre = cliente.usuario
            ? `${cliente.usuario.first_name || ''} ${cliente.usuario.last_name || ''}`.trim() || cliente.usuario.email
            : 'Cliente sin nombre';
        
        setClienteDeclaracion({
            nombre,
            email: cliente.usuario?.email,
            telefono: cliente.telefono,
            ci_nit: cliente.ci_nit,
            tipo_cliente: cliente.usuario?.tipo_nivel_usuario?.nombre || 'Cliente',
            fecha_declaracion_jurada: cliente.fecha_declaracion_jurada,
            foto_declaracion_jurada_url: cliente.foto_declaracion_jurada_url,
        });
        setDeclaracionModalOpen(true);
    };

    const filteredClientes = useMemo(() => {
        if (!searchTerm.trim()) return clientes;
        const q = searchTerm.toLowerCase();
        return clientes.filter(c => {
            const name = `${c.usuario?.first_name || ''} ${c.usuario?.last_name || ''}`.toLowerCase();
            const email = (c.usuario?.email || '').toLowerCase();
            const phone = (c.telefono || '').toLowerCase();
            const ci = (c.ci_nit || '').toLowerCase();
            const tipo = (c.usuario?.tipo_nivel_usuario?.nombre || '').toLowerCase();
            return name.includes(q) || email.includes(q) || phone.includes(q) || ci.includes(q) || tipo.includes(q);
        });
    }, [clientes, searchTerm]);

    const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedClientes = filteredClientes.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null) return <p className="p-8 text-center text-slate-500 font-medium">Cargando permisos…</p>;

    return (
        <div className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl pb-10">
            {/* Header & Breadcrumb */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-2">
                <div>
                    <nav className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 mb-1">
                        <a href="/dashboard" className="hover:text-[#8f1f64] transition-colors flex items-center gap-1">
                            <Home className="h-3.5 w-3.5" /> Dashboard
                        </a>
                        <ChevronRight className="h-3 w-3 text-slate-300" />
                        <span className="text-[#8f1f64] font-bold">Clientes</span>
                    </nav>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                        Directorio de <span className="text-[#8f1f64]">Clientes</span>
                    </h1>
                    <p className="text-xs text-slate-500 font-medium">
                        Gestión de clientes, niveles (Eventual, Recurrente, Afiliado) y control de declaraciones juradas
                    </p>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                    {perm.can_create && (
                        <button
                            onClick={() => setIsAddOpen(true)}
                            className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-[#8f1f64] hover:bg-[#c1348b] text-white text-xs font-bold transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Nuevo Cliente
                        </button>
                    )}
                </div>
            </div>

            {/* Main Table Card */}
            <div className="bg-white rounded-2xl shadow-xs border border-slate-200 overflow-hidden">
                {/* Search Bar & Stats */}
                <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50/50">
                    <div className="relative w-full sm:w-80">
                        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, CI, teléfono o nivel..."
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full pl-9 pr-4 py-2 bg-white text-xs rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] text-slate-800 placeholder-slate-400"
                        />
                    </div>
                    <div className="text-xs text-slate-500 font-medium self-end sm:self-center">
                        Total: <span className="font-bold text-slate-800">{filteredClientes.length}</span> clientes
                    </div>
                </div>

                <EditClienteModal
                    isOpen={isEditOpen}
                    onClose={() => setIsEditOpen(false)}
                    cliente={clienteToEdit}
                    onUpdate={handleUpdateCliente}
                />
                <AddClienteModal
                    isOpen={isAddOpen}
                    onClose={() => setIsAddOpen(false)}
                    onAddCliente={handleAddCliente}
                />
                <DeleteClienteModal
                    isOpen={isDeleteOpen}
                    onClose={() => setIsDeleteOpen(false)}
                    cliente={clienteToDelete}
                    onDeleted={handleClienteDeleted}
                />
                <ModalVerDeclaracionJurada
                    isOpen={declaracionModalOpen}
                    onClose={() => setDeclaracionModalOpen(false)}
                    cliente={clienteDeclaracion}
                />
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-50 border-b border-slate-200/80 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                            <tr>
                                <th className="px-5 py-3.5">Cliente</th>
                                <th className="px-4 py-3.5">Tipo / Nivel</th>
                                <th className="px-4 py-3.5">Contacto</th>
                                <th className="px-4 py-3.5">CI / NIT</th>
                                <th className="px-4 py-3.5 text-center">Declaración Jurada</th>
                                <th className="px-4 py-3.5 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-xs text-slate-700">
                            {perm.can_read && displayedClientes.map(c => {
                                const nombreCompleto = c.usuario
                                    ? `${c.usuario.first_name || ''} ${c.usuario.last_name || ''}`.trim() || 'Sin nombre'
                                    : 'Sin usuario';
                                const tipoNivel = c.usuario?.tipo_nivel_usuario?.nombre || 'Eventual';
                                const isFirmada = Boolean(c.declaracion_jurada_firmada);

                                return (
                                    <tr key={c.id} className="hover:bg-slate-50/80 transition-colors">
                                        {/* Cliente / Avatar */}
                                        <td className="px-5 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <div className="h-9 w-9 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center font-bold text-slate-600 text-xs flex-shrink-0">
                                                    {c.foto_url ? (
                                                        <img src={c.foto_url} alt={nombreCompleto} className="h-full w-full object-cover" />
                                                    ) : (
                                                        nombreCompleto.charAt(0).toUpperCase()
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="font-bold text-slate-900 truncate">{nombreCompleto}</p>
                                                    <p className="text-[11px] text-slate-400 truncate">{c.usuario?.email || '—'}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Tipo / Nivel */}
                                        <td className="px-4 py-3.5">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold ${
                                                tipoNivel.toLowerCase().includes('afiliado')
                                                    ? 'bg-[#8f1f64]/10 text-[#8f1f64] border border-[#8f1f64]/20'
                                                    : tipoNivel.toLowerCase().includes('recurrente')
                                                    ? 'bg-[#61257b]/10 text-[#61257b] border border-[#61257b]/20'
                                                    : 'bg-slate-100 text-slate-700 border border-slate-200'
                                            }`}>
                                                {tipoNivel}
                                            </span>
                                        </td>

                                        {/* Contacto */}
                                        <td className="px-4 py-3.5 font-medium text-slate-600">
                                            {c.telefono || '—'}
                                        </td>

                                        {/* CI / NIT */}
                                        <td className="px-4 py-3.5 font-medium text-slate-600">
                                            {c.ci_nit || '—'}
                                        </td>

                                        {/* Declaración Jurada Badge + Viewer */}
                                        <td className="px-4 py-3.5 text-center">
                                            {isFirmada ? (
                                                <button
                                                    onClick={() => handleVerDeclaracion(c)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors shadow-2xs group"
                                                    title="Ver declaración jurada y firma fotografiada"
                                                >
                                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                                    Firmada
                                                    {c.foto_declaracion_jurada_url && (
                                                        <Eye className="h-3 w-3 text-emerald-700 ml-0.5 opacity-60 group-hover:opacity-100" />
                                                    )}
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => openEditModal(c)}
                                                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                                                    title="Hacer clic para cargar la firma"
                                                >
                                                    <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                                                    Pendiente
                                                </button>
                                            )}
                                        </td>

                                        {/* Acciones */}
                                        <td className="px-4 py-3.5 text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                {isFirmada && (
                                                    <button
                                                        onClick={() => handleVerDeclaracion(c)}
                                                        className="p-1.5 rounded-lg text-[#61257b] hover:bg-[#61257b]/10 transition-colors"
                                                        title="Ver foto de declaración jurada"
                                                    >
                                                        <FileCheck className="h-4 w-4" />
                                                    </button>
                                                )}
                                                {perm.can_update && (
                                                    <button
                                                        onClick={() => openEditModal(c)}
                                                        className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
                                                        title="Editar cliente"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                )}
                                                {perm.can_delete && (
                                                    <button
                                                        onClick={() => openDeleteModal(c)}
                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                        title="Eliminar cliente"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}

                            {perm.can_read && filteredClientes.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                                        <Users className="h-10 w-10 mx-auto mb-2 text-slate-300 stroke-1" />
                                        <p className="text-sm font-semibold text-slate-700">No se encontraron clientes</p>
                                        <p className="text-xs text-slate-400 mt-0.5">Prueba con otro término de búsqueda o agrega un nuevo cliente.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {perm.can_read && filteredClientes.length > 0 && (
                    <div className="p-4 border-t border-slate-100">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            itemsPerPage={itemsPerPage}
                            totalItems={filteredClientes.length}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientsPage;
